'use client'
import * as React from 'react'
import Card from '@mui/material/Card'
import CardActions from '@mui/material/CardActions'
import CardContent from '@mui/material/CardContent'
import Button from '@mui/material/Button'
import Typography from '@mui/material/Typography'
import { v4 as uuidv4 } from 'uuid'
import { SxProps, styled } from '@mui/material'
import { dFlexCenter } from '../../../Theme/UtilityStyles'
import DoneIcon from '@mui/icons-material/Done'
import { colors } from '../../../Theme/LegacyTheme'

interface CustomCardDataProps {
  icon?: React.ReactNode
  title: string
  description: string
  buttonText?: string
  buttonLink?: string
  // ButtonId required for Google Analytics Tracking. Optional as we don't always use the button in this component
  buttonId?: string
}

interface CustomCardProps {
  cardData: CustomCardDataProps[]
  headingVariant?: 'h2' | 'h3' | 'h4'
  cardsSideBySide?: boolean
  styleOverrides?: SxProps
  withBoxShadow?: boolean
}

const MuiCardsWrapper = styled('div')(({ theme }) => ({
  '& .MuiCard-root': {
    marginBottom: theme.spacing(4),
    [theme.breakpoints.up('md')]: {
      maxWidth: 370,
      marginBottom: 0
    },
    '&:last-of-type': {
      marginBottom: 0
    }
  },
  '& .card-icon': {
    ...dFlexCenter,
    backgroundColor: colors.lightGreen,
    width: theme.spacing(7),
    height: theme.spacing(7),
    borderRadius: '100%',
    marginBottom: theme.spacing(4)
  },
  '&.side-by-side': {
    ...dFlexCenter,
    flexDirection: 'column',
    [theme.breakpoints.up('md')]: {
      flexDirection: 'row',
      alignItems: 'stretch',
      justifyContent: 'stretch'
    },
    '& .MuiCard-root': {
      [theme.breakpoints.up('md')]: {
        minWidth: 'calc(33.33% - 20px)',
        display: 'flex',
        flexDirection: 'column',
        wordBreak: 'break-word'
      }
    },
    '& .MuiCardActions-root': {
      [theme.breakpoints.up('md')]: {
        marginTop: 'auto'
      },
      a: {
        '&:hover': {
          color: colors.hoverBlue
        }
      }
    },
    '& > .MuiCard-root:not(:last-of-type)': {
      [theme.breakpoints.up('md')]: {
        marginRight: theme.spacing(5)
      }
    }
  }
}))

const CardWithButton: React.FC<CustomCardProps> = ({
  cardData,
  cardsSideBySide,
  headingVariant,
  styleOverrides,
  withBoxShadow
}) => {
  const boxShadow = withBoxShadow ? { boxShadow: '0 7px 50px -20px rgba(0,0,0,.2)' } : ''

  return (
    <MuiCardsWrapper
      className={cardsSideBySide ? 'side-by-side' : ''}
      data-testid="mui-cards-wrapper"
    >
      {cardData.map((item, index) => (
        <Card
          key={uuidv4()}
          sx={{ padding: (theme) => theme.spacing(4), ...styleOverrides, ...boxShadow }}
          data-testid={`card-${index}`}
        >
          <CardContent sx={{ padding: 0 }}>
            <div className="card-icon">
              {item.icon ?? <DoneIcon sx={{ color: colors.darkGreen, fontSize: 30 }} />}
            </div>
            <Typography variant={headingVariant}>{item.title}</Typography>
            <Typography variant="body2">{item.description}</Typography>
          </CardContent>
          <CardActions sx={{ padding: 0 }}>
            {item.buttonText && (
              <Button href={item.buttonLink} sx={{ width: '100%' }} id={item.buttonId}>
                {item.buttonText}
              </Button>
            )}
          </CardActions>
        </Card>
      ))}
    </MuiCardsWrapper>
  )
}

export default CardWithButton
